var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
        r = Reflect.decorate(decorators, target, key, desc);
    else
        for (var i = decorators.length - 1; i >= 0; i--)
            if (d = decorators[i])
                r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function")
        return Reflect.metadata(k, v);
};
/* eslint-disable @stencil-community/own-props-must-be-private */
/* eslint-disable @stencil-community/own-methods-must-be-private */
/* eslint-disable @stencil-community/no-unused-watch */
import { html, LitElement, nothing } from "lit";
import { property } from "lit/decorators/property.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { repeat } from "lit/directives/repeat.js";
import { tokenMap } from "../../../common/utils";
import { DEFAULT_ASSISTANT_STATUS, getMessageContent, getMessageFilesAndSources } from "./../utils";
import { renderContentBySections } from "./renders/renders.lit";
// import styles from "../chat.scss?inline";
const getAriaBusyValue = (status) => (status === "streaming" ? "true" : "false");
export class ChChatLit extends LitElement {
    #cellHasToReserveSpace_accessor_storage;
    /**
     * Specifies a Set of cells that must render an additional div to correctly
     * reserve space. This space is used to align the cell at the start scroll
     * position.
     *
     * Even if only one cell is aligned to the start of the scroll, all cells
     * that participated in this behavior must maintain the additional rendered
     * div to avoid destroying the DOM and thus causing some side effects on
     * elements whose state the chat can not control.
     */
    get cellHasToReserveSpace() { return this.#cellHasToReserveSpace_accessor_storage; }
    set cellHasToReserveSpace(value) { this.#cellHasToReserveSpace_accessor_storage = value; }
    #cellIdAlignedWhenRendered_accessor_storage;
    get cellIdAlignedWhenRendered() { return this.#cellIdAlignedWhenRendered_accessor_storage; }
    set cellIdAlignedWhenRendered(value) { this.#cellIdAlignedWhenRendered_accessor_storage = value; }
    #chatRef_accessor_storage = undefined;
    /**
     * Specifies the reference for the ch-chat parent.
     */
    get chatRef() { return this.#chatRef_accessor_storage; } // TODO: This is a WA to avoid a type error in runtime
    set chatRef(value) { this.#chatRef_accessor_storage = value; }
    #liveKitMessages_accessor_storage = [];
    /**
     * Specifies the live kit messages that are rendered when liveMode = true in
     * the `ch-chat`
     */
    get liveKitMessages() { return this.#liveKitMessages_accessor_storage; }
    set liveKitMessages(value) { this.#liveKitMessages_accessor_storage = value; }
    #newUserMessageAlignment_accessor_storage = "end";
    /**
     * Specifies how the messages added by the user interaction will be aligned
     * in the chat.
     *
     * If `newUserMessageAlignment === "start"` the chat will reserve the
     * necessary space to visualize the message at the start of the content
     * viewport if the content is not large enough.
     * This behavior is the same as the Monaco editor does for reserving space
     * when visualizing the last lines positioned at the top of the editor.
     */
    get newUserMessageAlignment() { return this.#newUserMessageAlignment_accessor_storage; }
    set newUserMessageAlignment(value) { this.#newUserMessageAlignment_accessor_storage = value; }
    #newUserMessageScrollBehavior_accessor_storage = "instant";
    /**
     * Specifies how the chat will scroll to the position of the messages added
     * by user interaction.
     */
    get newUserMessageScrollBehavior() { return this.#newUserMessageScrollBehavior_accessor_storage; }
    set newUserMessageScrollBehavior(value) { this.#newUserMessageScrollBehavior_accessor_storage = value; }
    #renderItem_accessor_storage;
    /**
     * This property allows us to implement custom rendering of chat items.
     *
     * This works by providing a custom render of the cell content in two
     * possible ways:
     *   1. Replacing the render of the entire cell with a function of the
     *   message model.
     *
     *   2. Replacing the render of specific parts of the message by providing an
     *   object with the specific renders of the message sections (`codeBlock`,
     *   `content`, `files` and/or `messageStructure`).
     */
    get renderItem() { return this.#renderItem_accessor_storage; }
    set renderItem(value) { this.#renderItem_accessor_storage = value; }
    #smartGridRef_accessor_storage;
    /**
     * Specifies the reference for the smart grid parent.
     *
     * This property is useful to avoid the cell from queering the ch-smart-grid
     * ref on the initial load.
     */
    get smartGridRef() { return this.#smartGridRef_accessor_storage; } // TODO: This is a WA to avoid a type error in runtime
    set smartGridRef(value) { this.#smartGridRef_accessor_storage = value; }
    #virtualItems_accessor_storage = [];
    /**
     * Specifies the virtual items that the chat will display.
     */
    get virtualItems() { return this.#virtualItems_accessor_storage; }
    set virtualItems(value) { this.#virtualItems_accessor_storage = value; }
    // "Shadow DOM === false"
    createRenderRoot() {
        return this;
    }
    #alignCellWhenRendered = () => 
    // TODO: This is a WA to avoid a type error in runtime
    this.smartGridRef.scrollEndContentToPosition(this.cellIdAlignedWhenRendered, {
        position: this.newUserMessageAlignment,
        behavior: this.newUserMessageScrollBehavior
    });
    #getMessageRenderedContent = (message) => {
        // Default render
        if (!this.renderItem) {
            return renderContentBySections(message, this.chatRef, {});
        }
        return typeof this.renderItem === "function"
            ? this.renderItem(message)
            : renderContentBySections(message, this.chatRef, this.renderItem); // The custom render is separated by sections
    };
    #renderItem = (message) => {
        if (message.role === "system") {
            return "";
        }
        const isAssistantMessage = message.role === "assistant";
        const filesAndSources = getMessageFilesAndSources(message);
        const parts = tokenMap({
            [`message ${message.role} ${message.id}`]: true,
            "has-content": (getMessageContent(message) ?? "").trim() !== "",
            "has-files": filesAndSources.files.length !== 0,
            "has-sources": filesAndSources.sources.length !== 0,
            [message.parts]: !!message.parts,
            [message.status ??
                DEFAULT_ASSISTANT_STATUS]: isAssistantMessage
        });
        const renderedContent = this.#getMessageRenderedContent(message);
        const messageIsCellAlignedAtTheStart = message.id === this.cellIdAlignedWhenRendered;
        const hasToRenderAnExtraDiv = this.cellHasToReserveSpace !== undefined &&
            this.cellHasToReserveSpace.has(message.id);
        return html `<ch-smart-grid-cell
      .cellId=${message.id}
      aria-live=${ifDefined(isAssistantMessage ? "polite" : undefined)}
      aria-busy=${
        // Wait until all changes are made to prevents assistive
        // technologies from announcing changes before updates are done
        ifDefined(isAssistantMessage ? getAriaBusyValue(message.status) : undefined)}
      part=${ifDefined(hasToRenderAnExtraDiv ? undefined : parts)}
      .smartGridRef=${
        // TODO: This is a WA to avoid a type error in runtime
        this.smartGridRef}
      @smartCellDidLoad=${messageIsCellAlignedAtTheStart
            ? this.#alignCellWhenRendered
            : undefined}
    >
      ${hasToRenderAnExtraDiv
            ? html `<div part=${parts}>${renderedContent}</div>`
            : renderedContent}
    </ch-smart-grid-cell>`;
    };
    render() {
        return html `${repeat(this.virtualItems, item => item.id, this.#renderItem)}${this.liveKitMessages
            ? repeat(this.liveKitMessages, item => item.id, this.#renderItem)
            : nothing}`;
    }
}
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Set)
], ChChatLit.prototype, "cellHasToReserveSpace", null);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", String)
], ChChatLit.prototype, "cellIdAlignedWhenRendered", null);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", HTMLDivElement)
], ChChatLit.prototype, "chatRef", null);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Array)
], ChChatLit.prototype, "liveKitMessages", null);
__decorate([
    property(),
    __metadata("design:type", String)
], ChChatLit.prototype, "newUserMessageAlignment", null);
__decorate([
    property(),
    __metadata("design:type", Object)
], ChChatLit.prototype, "newUserMessageScrollBehavior", null);
__decorate([
    property(),
    __metadata("design:type", Object)
], ChChatLit.prototype, "renderItem", null);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", HTMLDivElement)
], ChChatLit.prototype, "smartGridRef", null);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Array)
], ChChatLit.prototype, "virtualItems", null);
customElements.define("ch-chat-lit", ChChatLit);
